import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import TrustBar from './components/TrustBar';
import ProblemSolution from './components/ProblemSolution';
import FeatureHighlight from './components/FeatureHighlight';
import SystemOverview from './components/SystemOverview';
import StatsSection from './components/StatsSection';
import Industries from './components/Industries';
import HowItWorks from './components/HowItWorks';
import ContactModal from './components/ContactModal';

function CaseStudySection() {
  return (
    <section className="bg-[#F8FAFC] dark:bg-[#0C1322] overflow-hidden">
      <div className="max-w-7xl mx-auto px-8 lg:px-16 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="rounded-2xl overflow-hidden h-[420px]">
            <img
              src="https://public.readdy.ai/ai/img_res/76de499b1099362a8328b325d1a166e7.jpg"
              alt="Client success story"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div>
            <span className="text-xs tracking-widest uppercase text-[#2563EB] font-medium block mb-6">
              Client Outcomes
            </span>
            <h2 className="text-4xl lg:text-5xl font-semibold text-[#0B2A4A] dark:text-white tracking-[-0.025em] leading-[1.1] mb-7">
              From Missed<br />Opportunities to<br />Structured Growth.
            </h2>
            <p className="text-base text-[#0B2A4A]/65 dark:text-white/60 leading-relaxed tracking-wide mb-10 max-w-md">
              Clients typically see faster response times, increased booking rates,
              and improved operational clarity within weeks of going live.
            </p>

            <div className="grid grid-cols-2 gap-6">
              {[
                { value: '40%', label: 'reduction in missed inquiries' },
                { value: '2×', label: 'increase in booked appointments' },
                { value: '< 1 min', label: 'average AI response time' },
                { value: '3 wks', label: 'average deployment timeline' },
              ].map((item) => (
                <div key={item.label} className="border-l-2 border-[#2563EB]/30 dark:border-[#2563EB]/50 pl-4">
                  <div className="text-2xl font-semibold text-[#0B2A4A] dark:text-white tracking-tight">{item.value}</div>
                  <div className="text-xs text-[#0B2A4A]/55 dark:text-white/45 mt-0.5 tracking-wide">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaSection({ onCtaClick }: { onCtaClick: () => void }) {
  return (
    <section
      id="contact"
      className="relative overflow-hidden"
    >
      {/* Full-bleed dark background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20dark%20deep%20navy%20night%20cityscape%20aerial%20abstract%20premium%20professional%20minimal%20dark%20dramatic%20luxury%20editorial%20atmosphere%20calm%20structured&width=1920&height=700&seq=henexa-cta-v2&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#050D18]/80" />
        <div
          className="absolute inset-0 pointer-events-none opacity-15"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-16 py-36 text-center">
        <span className="text-xs tracking-widest uppercase text-white/35 font-medium block mb-8">
          Get Started
        </span>
        <h2 className="text-5xl lg:text-7xl font-semibold text-white tracking-[-0.03em] leading-[1.0] mb-8">
          Bring Structure<br />to Your Operations.
        </h2>
        <p className="text-lg text-white/55 max-w-lg mx-auto mb-12 leading-relaxed tracking-wide">
          Replace manual processes with intelligent systems that run continuously
          and deliver results from day one.
        </p>
        <button
          onClick={onCtaClick}
          className="px-10 py-4 bg-[#2563EB] text-white text-sm font-medium tracking-wide rounded-full hover:bg-[#1E3A8A] transition-all cursor-pointer whitespace-nowrap hover:scale-[1.02] active:scale-[0.98]"
        >
          Start Your System
        </button>

        {/* Trust indicators */}
        <div className="mt-16 flex items-center justify-center gap-12 flex-wrap">
          {[
            { icon: 'ri-timer-flash-line', text: 'Live in 2–3 Weeks' },
            { icon: 'ri-shield-check-line', text: 'Secure & Compliant' },
            { icon: 'ri-refresh-line', text: 'Continuously Optimized' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-white/45">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${item.icon} text-sm`} />
              </div>
              <span className="text-xs tracking-wide">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="font-sans bg-white dark:bg-[#070D1B] min-w-[1024px]">
      <Navbar onCtaClick={() => setModalOpen(true)} />
      <main>
        <HeroSection onCtaClick={() => setModalOpen(true)} />
        <TrustBar />
        <ProblemSolution />
        <FeatureHighlight onCtaClick={() => setModalOpen(true)} />
        <SystemOverview />
        <StatsSection />
        <Industries />
        <CaseStudySection />
        <HowItWorks />
        <CtaSection onCtaClick={() => setModalOpen(true)} />
      </main>
      <Footer />
      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
